import React from 'react';
import { Link } from 'react-router-dom';

// Import CEO image
import ceoImage from '../assets/images/team/ceo-photo.png';

function About() {
  return (
    <>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #000000 50%, #0a0a0a 100%)',
        padding: '8rem 0 6rem',
        position: 'relative',
        overflow: 'hidden',
        borderBottom: '1px solid rgba(168, 85, 247, 0.2)'
      }}>
        {/* Animated background effects */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 30% 50%, rgba(168, 85, 247, 0.2) 0%, transparent 50%)',
          animation: 'pulseGradient 8s ease-in-out infinite',
          pointerEvents: 'none'
        }}></div>

        <div style={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(236, 72, 153, 0.15) 0%, transparent 70%)',
          top: '-200px',
          right: '-100px',
          filter: 'blur(120px)',
          animation: 'floatLight 20s ease-in-out infinite',
          pointerEvents: 'none'
        }}></div>

        <style>
          {`
            @keyframes pulseGradient {
              0%, 100% { opacity: 0.3; transform: scale(1); }
              50% { opacity: 0.6; transform: scale(1.1); }
            }
            @keyframes floatLight {
              0%, 100% { transform: translate(0, 0) rotate(0deg); }
              50% { transform: translate(-100px, 100px) rotate(180deg); }
            }
            @keyframes gradientShift {
              0%, 100% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
            }
          `}
        </style>

        <div className="container" style={{ maxWidth: '1000px', position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <div data-aos="fade-down" style={{ marginBottom: '2rem' }}>
            <span style={{
              display: 'inline-block',
              padding: '0.6rem 1.5rem',
              background: 'rgba(168, 85, 247, 0.2)',
              border: '1px solid rgba(168, 85, 247, 0.4)',
              borderRadius: '50px',
              color: '#a855f7',
              fontSize: '0.95rem',
              fontWeight: '600',
              fontFamily: "'Space Grotesk', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              boxShadow: '0 0 20px rgba(168, 85, 247, 0.2)'
            }}>
              About Us
            </span>
          </div>

          <h1 data-aos="fade-up" style={{
            fontSize: 'clamp(3rem, 6vw, 5rem)',
            color: 'white',
            marginBottom: '2rem',
            fontWeight: '300',
            lineHeight: '1.1',
            fontFamily: "'Space Grotesk', sans-serif",
            textShadow: '0 0 60px rgba(168, 85, 247, 0.4)',
            letterSpacing: '-2px'
          }}>
            Pioneering the Future of
            <br />
            <span style={{
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 50%, #a855f7 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'gradientShift 3s ease infinite'
            }}>
              Enterprise AI
            </span>
          </h1>

          <p data-aos="fade-up" data-aos-delay="100" style={{
            fontSize: '1.35rem',
            color: 'rgba(255, 255, 255, 0.8)',
            maxWidth: '800px',
            margin: '0 auto',
            lineHeight: '1.7',
            fontFamily: "'Inter', sans-serif",
            fontWeight: '300'
          }}>
            We're on a mission to democratize artificial intelligence, making enterprise-grade AI accessible, 
            secure, and transformative for organizations worldwide.
          </p>
        </div>
      </section>

      {/* Company Stats */}
      <section style={{ padding: '5rem 0', background: '#fafafa' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div data-aos="fade-up" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '3rem',
            textAlign: 'center'
          }}>
            {[
              { 
                number: '500+', 
                label: 'Enterprise Clients',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18M9 21V9" />
                  </svg>
                )
              },
              { 
                number: '2.5M+', 
                label: 'Daily AI Queries',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                  </svg>
                )
              },
              { 
                number: '99.9%', 
                label: 'Uptime SLA',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                  </svg>
                )
              },
              { 
                number: '50+', 
                label: 'Countries Served',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                  </svg>
                )
              }
            ].map((stat, idx) => (
              <div key={idx} style={{
                padding: '2rem',
                background: 'white',
                borderRadius: '16px',
                border: '1px solid rgba(168, 85, 247, 0.1)',
                boxShadow: '0 4px 12px rgba(168, 85, 247, 0.08)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(168, 85, 247, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(168, 85, 247, 0.08)';
              }}
              >
                <div style={{ 
                  marginBottom: '1rem',
                  color: '#a855f7'
                }}>
                  {stat.icon}
                </div>
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: '300',
                  fontFamily: "'Space Grotesk', sans-serif",
                  background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '0.5rem'
                }}>
                  {stat.number}
                </div>
                <div style={{
                  fontSize: '0.95rem',
                  color: '#666',
                  fontFamily: "'Inter', sans-serif"
                }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section style={{ padding: '8rem 0', background: '#000000', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 70% 50%, rgba(168, 85, 247, 0.1) 0%, transparent 60%)',
          pointerEvents: 'none'
        }}></div>

        <div className="container" style={{ maxWidth: '1200px', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6rem', alignItems: 'center' }}>
            <div data-aos="fade-right">
              <span style={{
                display: 'inline-block',
                padding: '0.5rem 1.25rem',
                background: 'rgba(168, 85, 247, 0.15)',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                borderRadius: '50px',
                color: '#a855f7',
                fontSize: '0.9rem',
                fontWeight: '600',
                fontFamily: "'Space Grotesk', sans-serif",
                marginBottom: '1.5rem'
              }}>
                Our Vision
              </span>

              <h2 style={{
                fontSize: '3rem',
                color: 'white',
                marginBottom: '2rem',
                fontWeight: '300',
                fontFamily: "'Space Grotesk', sans-serif",
                lineHeight: '1.2'
              }}>
                A World Where AI
                <br />
                <span style={{
                  background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Empowers Everyone
                </span>
              </h2>

              <p style={{
                fontSize: '1.15rem',
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: '1.8',
                marginBottom: '2rem',
                fontFamily: "'Inter', sans-serif",
                fontWeight: '300'
              }}>
                We envision a future where artificial intelligence is not a luxury reserved for tech giants, 
                but a fundamental tool accessible to every organization—regardless of size or industry.
              </p>

              <p style={{
                fontSize: '1.15rem',
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: '1.8',
                fontFamily: "'Inter', sans-serif",
                fontWeight: '300'
              }}>
                Our vision is to create a world where businesses can harness the full potential of their data, 
                make intelligent decisions in real-time, and deliver unprecedented value to their customers—all 
                while maintaining the highest standards of security and privacy.
              </p>
            </div>

            <div data-aos="fade-left" style={{
              padding: '3rem',
              background: 'rgba(168, 85, 247, 0.05)',
              borderRadius: '20px',
              border: '1px solid rgba(168, 85, 247, 0.2)',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ display: 'grid', gap: '2rem' }}>
                {[
                  { 
                    title: 'Democratize AI', 
                    desc: 'Make enterprise AI accessible to organizations of all sizes',
                    icon: (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="32" height="32">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M12 1v6m0 6v6m5.2-14.8l-4.2 4.2m-2 2l-4.2 4.2M23 12h-6m-6 0H1m14.8 5.2l-4.2-4.2m-2-2l-4.2-4.2" />
                      </svg>
                    )
                  },
                  { 
                    title: 'Privacy First', 
                    desc: 'Build trust through uncompromising security and data sovereignty',
                    icon: (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="32" height="32">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                    )
                  },
                  { 
                    title: 'Innovation', 
                    desc: 'Push boundaries of what\'s possible with artificial intelligence',
                    icon: (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="32" height="32">
                        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                        <circle cx="12" cy="12" r="1" fill="currentColor" />
                      </svg>
                    )
                  },
                  { 
                    title: 'Partnership', 
                    desc: 'Grow together with our clients as trusted technology partners',
                    icon: (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="32" height="32">
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" />
                      </svg>
                    )
                  }
                ].map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
                    <div style={{
                      flexShrink: 0,
                      width: '60px',
                      height: '60px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(168, 85, 247, 0.1)',
                      borderRadius: '12px',
                      color: '#a855f7'
                    }}>
                      {item.icon}
                    </div>
                    <div>
                      <h4 style={{
                        fontSize: '1.25rem',
                        color: 'white',
                        marginBottom: '0.5rem',
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontWeight: '500'
                      }}>
                        {item.title}
                      </h4>
                      <p style={{
                        fontSize: '0.95rem',
                        color: 'rgba(255, 255, 255, 0.7)',
                        margin: 0,
                        fontFamily: "'Inter', sans-serif"
                      }}>
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section style={{ padding: '8rem 0', background: '#fafafa' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6rem', alignItems: 'center' }}>
            <div data-aos="fade-right" style={{
              padding: '3rem',
              background: 'white',
              borderRadius: '20px',
              border: '1px solid rgba(168, 85, 247, 0.1)',
              boxShadow: '0 8px 32px rgba(168, 85, 247, 0.1)'
            }}>
              <div style={{ display: 'grid', gap: '2.5rem' }}>
                {[
                  {
                    number: '01',
                    title: 'Simplify Complexity',
                    desc: 'Transform complex AI technology into intuitive, user-friendly solutions that anyone can deploy and manage.'
                  },
                  {
                    number: '02',
                    title: 'Accelerate Innovation',
                    desc: 'Enable organizations to implement AI in weeks, not years, accelerating their digital transformation journey.'
                  },
                  {
                    number: '03',
                    title: 'Ensure Security',
                    desc: 'Provide military-grade security and compliance frameworks that protect sensitive enterprise data.'
                  },
                  {
                    number: '04',
                    title: 'Drive Results',
                    desc: 'Deliver measurable ROI through intelligent automation and data-driven decision making.'
                  }
                ].map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '1.5rem' }}>
                    <div style={{
                      fontSize: '1.5rem',
                      fontWeight: '300',
                      fontFamily: "'Space Grotesk', sans-serif",
                      background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      flexShrink: 0
                    }}>
                      {item.number}
                    </div>
                    <div>
                      <h4 style={{
                        fontSize: '1.25rem',
                        color: '#1a1a1a',
                        marginBottom: '0.75rem',
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontWeight: '500'
                      }}>
                        {item.title}
                      </h4>
                      <p style={{
                        fontSize: '0.95rem',
                        color: '#666',
                        margin: 0,
                        lineHeight: '1.6',
                        fontFamily: "'Inter', sans-serif"
                      }}>
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div data-aos="fade-left">
              <span style={{
                display: 'inline-block',
                padding: '0.5rem 1.25rem',
                background: 'rgba(168, 85, 247, 0.15)',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                borderRadius: '50px',
                color: '#a855f7',
                fontSize: '0.9rem',
                fontWeight: '600',
                fontFamily: "'Space Grotesk', sans-serif",
                marginBottom: '1.5rem'
              }}>
                Our Mission
              </span>

              <h2 style={{
                fontSize: '3rem',
                color: '#1a1a1a',
                marginBottom: '2rem',
                fontWeight: '300',
                fontFamily: "'Space Grotesk', sans-serif",
                lineHeight: '1.2'
              }}>
                Transforming Data Into
                <br />
                <span style={{
                  background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Intelligent Action
                </span>
              </h2>

              <p style={{
                fontSize: '1.15rem',
                color: '#666',
                lineHeight: '1.8',
                marginBottom: '2rem',
                fontFamily: "'Inter', sans-serif",
                fontWeight: '300'
              }}>
                Our mission is to empower enterprises with AI solutions that are secure, scalable, and simple to implement. 
                We believe that every organization should have the ability to unlock the value hidden within their data.
              </p>

              <p style={{
                fontSize: '1.15rem',
                color: '#666',
                lineHeight: '1.8',
                fontFamily: "'Inter', sans-serif",
                fontWeight: '300'
              }}>
                Through cutting-edge technology, unwavering commitment to security, and customer-centric innovation, 
                we're building the foundation for an AI-powered future.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section style={{ padding: '8rem 0', background: '#000000', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(168, 85, 247, 0.1) 0%, transparent 70%)',
          pointerEvents: 'none'
        }}></div>

        <div className="container" style={{ maxWidth: '1200px', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: '5rem' }} data-aos="fade-up">
            <span style={{
              display: 'inline-block',
              padding: '0.5rem 1.25rem',
              background: 'rgba(168, 85, 247, 0.15)',
              border: '1px solid rgba(168, 85, 247, 0.3)',
              borderRadius: '50px',
              color: '#a855f7',
              fontSize: '0.9rem',
              fontWeight: '600',
              fontFamily: "'Space Grotesk', sans-serif",
              marginBottom: '1.5rem'
            }}>
              Core Values
            </span>

            <h2 style={{
              fontSize: '3rem',
              color: 'white',
              marginBottom: '1.5rem',
              fontWeight: '300',
              fontFamily: "'Space Grotesk', sans-serif"
            }}>
              What Drives Us Forward
            </h2>

            <p style={{
              fontSize: '1.15rem',
              color: 'rgba(255, 255, 255, 0.7)',
              maxWidth: '700px',
              margin: '0 auto',
              fontFamily: "'Inter', sans-serif"
            }}>
              Our values shape every decision we make and every solution we build
            </p>
          </div>

          <div data-aos="fade-up" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {[
              {
                title: 'Innovation First',
                desc: 'We continuously push boundaries, exploring new technologies and methodologies to deliver cutting-edge AI solutions.',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="36" height="36">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                )
              },
              {
                title: 'Security Always',
                desc: 'We never compromise on security. Every feature, every line of code is built with security at its core.',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="36" height="36">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                )
              },
              {
                title: 'Customer Success',
                desc: 'Your success is our success. We\'re committed to being true partners in your AI transformation journey.',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="36" height="36">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                  </svg>
                )
              },
              {
                title: 'Continuous Learning',
                desc: 'We invest in our team and technology, staying ahead of the curve in the rapidly evolving AI landscape.',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="36" height="36">
                    <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
                  </svg>
                )
              },
              {
                title: 'Sustainable Growth',
                desc: 'We build for the long term, creating sustainable solutions that scale with your business needs.',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="36" height="36">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                  </svg>
                )
              },
              {
                title: 'Excellence',
                desc: 'We maintain the highest standards in everything we do, from code quality to customer support.',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="36" height="36">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )
              }
            ].map((value, idx) => (
              <div key={idx} style={{
                padding: '2.5rem',
                background: 'rgba(168, 85, 247, 0.05)',
                borderRadius: '16px',
                border: '1px solid rgba(168, 85, 247, 0.2)',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(168, 85, 247, 0.1)';
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(168, 85, 247, 0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.2)';
              }}
              >
                <div style={{
                  marginBottom: '1.5rem',
                  color: '#a855f7'
                }}>
                  {value.icon}
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  color: 'white',
                  marginBottom: '1rem',
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: '400'
                }}>
                  {value.title}
                </h3>
                <p style={{
                  fontSize: '0.95rem',
                  color: 'rgba(255, 255, 255, 0.7)',
                  lineHeight: '1.7',
                  margin: 0,
                  fontFamily: "'Inter', sans-serif"
                }}>
                  {value.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Message from CEO */}
      <section style={{ padding: '8rem 0', background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div data-aos="fade-up" style={{
            background: 'white',
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(168, 85, 247, 0.15)',
            border: '1px solid rgba(168, 85, 247, 0.1)'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: '600px' }}>
              {/* CEO Image */}
              <div style={{
                position: 'relative',
                backgroundImage: `url(${ceoImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                alignItems: 'flex-end',
                padding: '3rem'
              }}>
                {/* Gradient Overlay - Reduced opacity */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.5), rgba(236, 72, 153, 0.5))',
                  zIndex: 1
                }}></div>

                {/* CEO Info Card */}
                <div style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  padding: '1.5rem 2rem',
                  borderRadius: '12px',
                  backdropFilter: 'blur(10px)',
                  position: 'relative',
                  zIndex: 2
                }}>
                  <h3 style={{
                    fontSize: '1.75rem',
                    color: '#1a1a1a',
                    marginBottom: '0.5rem',
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: '500'
                  }}>
                    Iris C Rusthique
                  </h3>
                  <p style={{
                    fontSize: '1rem',
                    color: '#a855f7',
                    margin: 0,
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: '500'
                  }}>
                    Founder & Chief Executive Officer
                  </p>
                </div>
              </div>

              {/* CEO Message */}
              <div style={{ padding: '4rem' }}>
                <span style={{
                  display: 'inline-block',
                  padding: '0.5rem 1.25rem',
                  background: 'rgba(168, 85, 247, 0.15)',
                  border: '1px solid rgba(168, 85, 247, 0.3)',
                  borderRadius: '50px',
                  color: '#a855f7',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  fontFamily: "'Space Grotesk', sans-serif",
                  marginBottom: '2rem'
                }}>
                  Message from the CEO
                </span>

                <h2 style={{
                  fontSize: '2.25rem',
                  color: '#1a1a1a',
                  marginBottom: '2rem',
                  fontWeight: '400',
                  fontFamily: "'Space Grotesk', sans-serif",
                  lineHeight: '1.3'
                }}>
                  "Building the Future of
                  <span style={{
                    background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}> Enterprise Intelligence"</span>
                </h2>

                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  <p style={{
                    fontSize: '1.05rem',
                    color: '#666',
                    lineHeight: '1.8',
                    margin: 0,
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: '300'
                  }}>
                    When we started this journey, we saw a fundamental problem: powerful AI technology was locked 
                    behind complexity, cost, and security concerns. Organizations had data, but couldn't unlock its potential.
                  </p>

                  <p style={{
                    fontSize: '1.05rem',
                    color: '#666',
                    lineHeight: '1.8',
                    margin: 0,
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: '300'
                  }}>
                    Today, we're proud to serve over 500 enterprises worldwide, processing millions of AI-powered 
                    queries daily. But we're just getting started. Our vision is to make AI as fundamental to business 
                    operations as email or cloud storage—secure, reliable, and accessible to all.
                  </p>

                  <p style={{
                    fontSize: '1.05rem',
                    color: '#666',
                    lineHeight: '1.8',
                    margin: 0,
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: '300'
                  }}>
                    Thank you for trusting us on this journey. Together, we're not just implementing technology—we're 
                    transforming how businesses think, work, and grow.
                  </p>

                  <div style={{
                    marginTop: '1rem',
                    paddingTop: '1.5rem',
                    borderTop: '1px solid rgba(168, 85, 247, 0.1)'
                  }}>
                    <p style={{
                      fontSize: '1.25rem',
                      fontFamily: "'Dancing Script', cursive",
                      color: '#a855f7',
                      margin: 0
                    }}>
                      Iris C Rusthique
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '8rem 0',
        background: 'linear-gradient(135deg, #000000 0%, #0a0a0a 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(168, 85, 247, 0.15) 0%, transparent 70%)',
          animation: 'pulseGradient 8s ease-in-out infinite',
          pointerEvents: 'none'
        }}></div>

        <div className="container" style={{ maxWidth: '900px', textAlign: 'center', position: 'relative', zIndex: 1 }} data-aos="fade-up">
          <h2 style={{
            fontSize: '3rem',
            color: 'white',
            marginBottom: '1.5rem',
            fontWeight: '300',
            fontFamily: "'Space Grotesk', sans-serif"
          }}>
            Ready to Transform Your Enterprise?
          </h2>

          <p style={{
            fontSize: '1.25rem',
            color: 'rgba(255, 255, 255, 0.7)',
            marginBottom: '3rem',
            fontFamily: "'Inter', sans-serif",
            fontWeight: '300'
          }}>
            Join hundreds of organizations leveraging AI to drive innovation and growth
          </p>

          <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1.25rem 3rem',
              background: 'linear-gradient(135deg, #a855f7, #ec4899)',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '50px',
              fontSize: '1.1rem',
              fontWeight: '600',
              fontFamily: "'Space Grotesk', sans-serif",
              boxShadow: '0 10px 30px rgba(168, 85, 247, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-3px)';
              e.target.style.boxShadow = '0 15px 50px rgba(168, 85, 247, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 10px 30px rgba(168, 85, 247, 0.3)';
            }}
            >
              Schedule Consultation
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>

            <Link to="/features" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1.25rem 3rem',
              background: 'rgba(168, 85, 247, 0.15)',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '50px',
              fontSize: '1.1rem',
              fontWeight: '600',
              fontFamily: "'Space Grotesk', sans-serif",
              border: '2px solid rgba(168, 85, 247, 0.5)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(168, 85, 247, 0.25)';
              e.target.style.transform = 'translateY(-3px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(168, 85, 247, 0.15)';
              e.target.style.transform = 'translateY(0)';
            }}
            >
              Explore Platform
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

export default About;
